import { Typography } from '@/components/atoms/Typography';
import {
  BedIcon,
  ExpandIcon,
  LandPlotWebIcon,
  ShowerHeadIcon,
} from '@/components/atoms/icons';
import { useUIColors } from '@/core/hooks';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { hasNumericValue } from '@/core/utils/fieldAvailability';
import { isSmallScreen } from '@/core/utils/responsive';
import { WebAmenityIcon } from '@/modules/property-details/components/WebAmenityIcon';
import {
  useWebAmenities,
  type WebAmenity,
} from '@/modules/property-details/hooks/useWebAmenities';
import React from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import type { UnitDetailsLabels } from '../constants/getPropertyLabels';
import { usePropertyLabels } from '../hooks/usePropertyLabels';

export interface UnitAboutAccordionProps {
  bedrooms?: number | undefined;
  bathrooms?: number | undefined;
  areaSqFt?: number | undefined;
  amenities: string[];
  onViewFloorPlan?: () => void;
  showFloorPlan?: boolean;
  floorPlanLoading?: boolean;
}

type UnitInfoIcon = React.ComponentType<{ size?: number; color?: string }>;

interface UnitInfoItem {
  key: string;
  node: React.JSX.Element;
}

interface UnitInfoItemViewProps {
  IconComponent: UnitInfoIcon;
  value: string;
  label: string;
  iconSize: number;
  isMobile: boolean;
  iconColor: string;
  valueVariant: 'headline' | 'title3';
  labelVariant: 'caption' | 'body';
}

function UnitInfoItemView({
  IconComponent,
  value,
  label,
  iconSize,
  isMobile,
  iconColor,
  valueVariant,
  labelVariant,
  isRTL,
}: Readonly<UnitInfoItemViewProps & { isRTL: boolean }>) {
  return (
    <View
      style={[
        styles.unitInfoItem,
        isMobile ? styles.unitInfoItemMobile : styles.amenityItemWeb,
      ]}
    >
      <View
        style={[
          styles.unitInfoItemHeader,
          isRTL && styles.unitInfoItemHeaderRTL,
        ]}
      >
        <IconComponent size={iconSize} color={iconColor} />
        <Typography variant={valueVariant} color="primary">
          {value}
        </Typography>
      </View>
      <Typography
        variant={labelVariant}
        color="secondary"
        style={[
          styles.amenityText,
          isMobile ? styles.amenityTextMobile : styles.amenityTextWeb,
        ]}
      >
        {label}
      </Typography>
    </View>
  );
}

function FloorPlanLink({
  labels,
  colors,
  isRTL,
  floorPlanLoading,
  onViewFloorPlan,
}: Readonly<{
  labels: UnitDetailsLabels;
  colors: ReturnType<typeof useUIColors>;
  isRTL: boolean;
  floorPlanLoading: boolean;
  onViewFloorPlan?: (() => void) | undefined;
}>) {
  return (
    <View style={styles.floorPlanWrapper}>
      <TouchableOpacity
        style={[styles.floorPlanButton, isRTL && styles.floorPlanButtonRTL]}
        onPress={floorPlanLoading ? undefined : onViewFloorPlan}
        disabled={floorPlanLoading}
        accessibilityRole="button"
        accessibilityLabel={labels.VIEW_FLOOR_PLAN}
        accessibilityState={{ disabled: floorPlanLoading }}
      >
        <LandPlotWebIcon size={18} color={colors.buttonDefault} />
        <Typography
          variant="headline"
          color="buttonDefault"
          numberOfLines={1}
          style={styles.floorPlanText}
        >
          {labels.VIEW_FLOOR_PLAN}
        </Typography>
      </TouchableOpacity>
      {floorPlanLoading ? (
        <View
          style={[
            styles.floorPlanLoadingOverlay,
            { backgroundColor: colors.backgroundLevel1 },
          ]}
        >
          <ActivityIndicator size="small" color={colors.fontPrimary} />
        </View>
      ) : null}
    </View>
  );
}

function buildUnitInfoItems(
  bedrooms: number | undefined,
  bathrooms: number | undefined,
  areaSqFt: number | undefined,
  labels: UnitDetailsLabels,
  itemProps: Omit<UnitInfoItemViewProps, 'IconComponent' | 'value' | 'label'> & {
    isRTL: boolean;
  },
  areaIconSize: number,
): UnitInfoItem[] {
  const items: UnitInfoItem[] = [];
  if (hasNumericValue(bedrooms)) {
    items.push({
      key: 'bedrooms',
      node: (
        <UnitInfoItemView
          {...itemProps}
          IconComponent={BedIcon}
          value={String(bedrooms)}
          label={labels.BEDROOMS}
        />
      ),
    });
  }
  if (hasNumericValue(bathrooms)) {
    items.push({
      key: 'bathrooms',
      node: (
        <UnitInfoItemView
          {...itemProps}
          IconComponent={ShowerHeadIcon}
          value={String(bathrooms)}
          label={labels.BATHROOMS}
        />
      ),
    });
  }
  if (hasNumericValue(areaSqFt)) {
    items.push({
      key: 'area',
      node: (
        <UnitInfoItemView
          {...itemProps}
          iconSize={areaIconSize}
          IconComponent={ExpandIcon}
          value={areaSqFt.toLocaleString()}
          label={labels.AREA_IN_SQFT}
        />
      ),
    });
  }
  return items;
}

function AmenityRows({
  amenities,
  amenitiesPerRow,
  isMobile,
  iconColor,
  labelVariant,
}: Readonly<{
  amenities: WebAmenity[];
  amenitiesPerRow: number;
  isMobile: boolean;
  iconColor: string;
  labelVariant: 'caption' | 'body';
}>) {
  const rows: React.JSX.Element[] = [];
  for (let start = 0; start < amenities.length; start += amenitiesPerRow) {
    const rowAmenities = amenities.slice(start, start + amenitiesPerRow);
    const rowKey = rowAmenities[0]?.value ?? 'amenity-row';
    rows.push(
      <View
        key={rowKey}
        style={[
          styles.amenitiesRow,
          !isMobile && styles.amenitiesRowWeb,
        ]}
      >
        {rowAmenities.map(({ value, label, iconUrl }) => {
          return (
            <View
              key={value}
              style={[styles.amenityItem, !isMobile && styles.amenityItemWeb]}
            >
              <WebAmenityIcon
                iconUrl={iconUrl}
                label={label}
                size={24}
                defaultColor={iconColor}
              />
              <Typography
                variant={labelVariant}
                color="secondary"
                style={[
                  styles.amenityText,
                  isMobile ? styles.amenityTextMobile : styles.amenityTextWeb,
                ]}
              >
                {label}
              </Typography>
            </View>
          );
        })}
        {isMobile &&
          rowAmenities.length < amenitiesPerRow &&
          ['a', 'b', 'c', 'd', 'e']
            .slice(0, amenitiesPerRow - rowAmenities.length)
            .map((fillerKey) => (
              <View key={`filler-${fillerKey}`} style={styles.amenityItem} />
            ))}
      </View>,
    );
  }
  return <>{rows}</>;
}

function UnitAboutUnitInfoSection({
  labels,
  colors,
  isMobile,
  isRTL,
  headingVariant,
  showFloorPlan,
  floorPlanLoading,
  onViewFloorPlan,
  unitInfoItems,
}: Readonly<{
  labels: UnitDetailsLabels;
  colors: ReturnType<typeof useUIColors>;
  isMobile: boolean;
  isRTL: boolean;
  headingVariant: 'subhead' | 'title3';
  showFloorPlan: boolean;
  floorPlanLoading: boolean;
  onViewFloorPlan?: (() => void) | undefined;
  unitInfoItems: UnitInfoItem[];
}>) {
  return (
    <View style={isMobile ? styles.sectionMobile : styles.unitInfoSection}>
      <View
        style={[styles.unitInfoHeader, isRTL && styles.unitInfoHeaderRTL]}
      >
        <View style={styles.unitInfoTitleWrap}>
          <Typography
            variant={headingVariant}
            color="primary"
            numberOfLines={1}
            style={isRTL && styles.sectionTitleRTL}
          >
            {labels.UNIT_INFO}
          </Typography>
        </View>
        {showFloorPlan ? (
          <FloorPlanLink
            labels={labels}
            colors={colors}
            isRTL={isRTL}
            floorPlanLoading={floorPlanLoading}
            onViewFloorPlan={onViewFloorPlan}
          />
        ) : null}
      </View>

      {unitInfoItems.length > 0 ? (
        <View
          style={[
            styles.amenitiesRow,
            !isMobile && styles.amenitiesRowWeb,
            isMobile && styles.unitInfoMobile,
            isRTL && styles.unitInfoRTL,
          ]}
        >
          {unitInfoItems.map((item) => (
            <React.Fragment key={item.key}>{item.node}</React.Fragment>
          ))}
        </View>
      ) : null}
    </View>
  );
}

function UnitAboutAmenitiesSection({
  labels,
  amenities,
  amenitiesPerRow,
  isMobile,
  isRTL,
  headingVariant,
  iconColor,
  labelVariant,
}: Readonly<{
  labels: UnitDetailsLabels;
  amenities: WebAmenity[];
  amenitiesPerRow: number;
  isMobile: boolean;
  isRTL: boolean;
  headingVariant: 'subhead' | 'title3';
  iconColor: string;
  labelVariant: 'caption' | 'body';
}>) {
  return (
    <View
      style={isMobile ? styles.amenitiesSectionMobile : styles.amenitiesSection}
    >
      <Typography
        variant={headingVariant}
        color="primary"
        style={isRTL && styles.sectionTitleRTL}
      >
        {labels.AMENITIES}
      </Typography>

      <View style={isMobile ? styles.amenitiesGridMobile : styles.amenitiesGrid}>
        <AmenityRows
          amenities={amenities}
          amenitiesPerRow={amenitiesPerRow}
          isMobile={isMobile}
          iconColor={iconColor}
          labelVariant={labelVariant}
        />
      </View>
    </View>
  );
}

/**
 * About Unit card: unit info (bedrooms/bathrooms/area), floor plan link, amenities.
 * Static panel (not accordion) — only one about section on unit details.
 */
export function UnitAboutAccordion({
  bedrooms,
  bathrooms,
  areaSqFt,
  amenities,
  onViewFloorPlan,
  showFloorPlan = true,
  floorPlanLoading = false,
}: Readonly<UnitAboutAccordionProps>) {
  const colors = useUIColors();
  const { isRTL } = useI18nContext();
  const isMobile = isSmallScreen();
  const amenitiesPerRow = isMobile ? 3 : 5;
  const normalizedAmenities = useWebAmenities(amenities);
  const { UNIT_DETAILS: labels } = usePropertyLabels();

  const headingVariant = isMobile ? 'subhead' : 'title3';
  const valueVariant = isMobile ? 'headline' : 'title3';
  const labelVariant = isMobile ? 'caption' : 'body';
  const infoIconSize = isMobile ? 20 : 24;
  const areaIconSize = isMobile ? 19 : 24;
  const contentStyle = isMobile ? styles.contentMobile : styles.content;

  const unitInfoItems = buildUnitInfoItems(
    bedrooms,
    bathrooms,
    areaSqFt,
    labels,
    {
      iconSize: infoIconSize,
      isMobile,
      iconColor: colors.fontPrimary,
      valueVariant,
      labelVariant,
      isRTL,
    },
    areaIconSize,
  );

  const showUnitInfoSection = unitInfoItems.length > 0 || showFloorPlan;

  return (
    <View style={[styles.card, { backgroundColor: colors.backgroundLevel1 }]}>
      <View style={contentStyle}>
        {showUnitInfoSection ? (
          <UnitAboutUnitInfoSection
            labels={labels}
            colors={colors}
            isMobile={isMobile}
            isRTL={isRTL}
            headingVariant={headingVariant}
            showFloorPlan={showFloorPlan}
            floorPlanLoading={floorPlanLoading}
            onViewFloorPlan={onViewFloorPlan}
            unitInfoItems={unitInfoItems}
          />
        ) : null}

        {normalizedAmenities.length > 0 ? (
          <UnitAboutAmenitiesSection
            labels={labels}
            amenities={normalizedAmenities}
            amenitiesPerRow={amenitiesPerRow}
            isMobile={isMobile}
            isRTL={isRTL}
            headingVariant={headingVariant}
            iconColor={colors.fontPrimary}
            labelVariant={labelVariant}
          />
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: 12,
    overflow: 'hidden',
    padding: 24,
  },
  content: {
    gap: 28,
  },
  contentMobile: {
    gap: 12,
  },
  section: {
    gap: 16,
  },
  /** Desktop: space between “Unit info” label and bedrooms/bathrooms/area row */
  unitInfoSection: {
    gap: 28,
  },
  /** Space between Amenities label and amenity icons */
  amenitiesSection: {
    gap: 16,
  },
  amenitiesSectionMobile: {
    gap: 16,
  },
  sectionMobile: {
    gap: 8,
  },
  sectionTitleRTL: {
    textAlign: 'right',
  },
  /** Figma: "Unit info" left, "View floor plan" trailing — always one row. */
  unitInfoHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'nowrap',
    gap: 12,
    width: '100%',
    display: 'flex',
  },
  unitInfoHeaderRTL: {
    flexDirection: 'row-reverse',
  },
  unitInfoTitleWrap: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    justifyContent: 'center',
  },
  unitInfoMobile: {
    gap: 8,
  },
  unitInfoRTL: {
    flexDirection: 'row-reverse',
  },
  unitInfoItem: {
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 8,
  },
  unitInfoItemMobile: {
    flex: 1,
    gap: 8,
  },
  unitInfoItemHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
  },
  unitInfoItemHeaderRTL: {
    flexDirection: 'row-reverse',
  },
  floorPlanWrapper: {
    position: 'relative',
    flexGrow: 0,
    flexShrink: 0,
    alignSelf: 'center',
  },
  floorPlanLoadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
  },
  floorPlanButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingVertical: 0,
    minHeight: 0,
    borderRadius: 4,
  },
  floorPlanButtonRTL: {
    flexDirection: 'row-reverse',
  },
  floorPlanText: {
    textDecorationLine: 'underline',
    lineHeight: 18,
  },
  amenitiesGrid: {
    gap: 8,
  },
  amenitiesGridMobile: {
    gap: 12,
  },
  amenitiesRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  // Desktop: no row gap — 5×20% widths + horizontal padding = fixed spacing
  amenitiesRowWeb: {
    gap: 0,
  },
  amenityItem: {
    flex: 1,
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 8,
  },
  /**
   * Desktop: lock each card to 1/5 of the row (Figma 5-col).
   * Fewer amenities keep that same width (no stretch). Padding gives ~8px between cards.
   * Centered icon sits slightly under the "M" of Amenities.
   * Note: avoid CSS calc() — RN Web collapses invalid widths and stacks items.
   */
  amenityItemWeb: {
    flex: 0,
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: '20%',
    width: '20%',
    maxWidth: '20%',
    alignItems: 'center',
    paddingHorizontal: 4,
    paddingTop: 16,
    paddingBottom: 8,
  },
  amenityText: {
    textAlign: 'center',
  },
  // Comfortable line height for wrapped labels (Typography defaults to 1:1)
  amenityTextWeb: {
    textAlign: 'center',
    lineHeight: 20,
  },
  amenityTextMobile: {
    lineHeight: 16,
  },
});

export default UnitAboutAccordion;
